import { Analytics } from '@setvi/shared/enums';

import { postActivitiesOnUnload } from './api';
import {
  FLUSH_AFTER_CLOSE_MS,
  MAX_ACTIVITY_ITEMS,
  WEB_APP_TYPE_ID
} from './constants';
import type { ActivityItem } from './types';

// Module-level on purpose: the buffer and the open view have to outlive any
// component, so that a view can still be closed from an unload handler.
let buffer: ActivityItem[] = [];

type PostActivities = (items: ActivityItem[]) => Promise<unknown>;

// The reporter registers its React Query mutation here, since the buffer is
// drained from a timer and from unload handlers rather than from a render.
let postActivities: PostActivities | null = null;

export const registerActivityPoster = (post: PostActivities | null) => {
  postActivities = post;
};

interface OpenView {
  objectId: string;
  typeId: Analytics;
  startedAt: number;
}

// Keyed rather than a single slot: two views can overlap for a moment while
// one dialog is being replaced by another, and a single slot would silently
// drop the first one.
const openViews = new Map<number, OpenView>();
let nextViewId = 0;

// Set while a post-close flush is pending, so a burst of closes coalesces.
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const toItem = (view: OpenView, endedAt: number): ActivityItem => ({
  TypeId: view.typeId,
  StartTime: new Date(view.startedAt).toISOString(),
  EndTime: new Date(endedAt).toISOString(),
  ObjectId: view.objectId,
  AppTypeId: WEB_APP_TYPE_ID
});

const cancelScheduledFlush = () => {
  if (flushTimer === null) return;
  clearTimeout(flushTimer);
  flushTimer = null;
};

export const queueActivity = (item: ActivityItem) => {
  buffer.push(item);
};

/**
 * Begin timing a view. Nothing is sent until it ends. The returned handle
 * identifies this view, so ending it cannot end somebody else's.
 */
export const startActivity = (objectId: string, typeId: Analytics): number => {
  nextViewId += 1;
  openViews.set(nextViewId, { objectId, typeId, startedAt: Date.now() });
  return nextViewId;
};

/**
 * Post everything buffered so far.
 *
 * The batch leaves the buffer before the request goes out and is not put back
 * on failure: the endpoint has no idempotency key, so a retry would insert the
 * same rows again and double-count. Losing a batch is the safer failure.
 */
export const flushActivities = ({ unload = false } = {}) => {
  cancelScheduledFlush();
  if (!buffer.length) return;

  const pending = buffer;
  buffer = [];

  for (let i = 0; i < pending.length; i += MAX_ACTIVITY_ITEMS) {
    const batch = pending.slice(i, i + MAX_ACTIVITY_ITEMS);
    // The keepalive request is also the fallback when no reporter is mounted,
    // so nothing is dropped just because the mutation is unavailable.
    if (unload || !postActivities) postActivitiesOnUnload(batch);
    else
      postActivities(batch).catch(() => {
        // Dropped on purpose — see above.
      });
  }
};

const scheduleFlush = () => {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushActivities();
  }, FLUSH_AFTER_CLOSE_MS);
};

/**
 * End one view and buffer it as one row. Ending a handle twice is a no-op, so
 * a dialog closing normally and an unload handler firing cannot both report
 * the same view.
 *
 * The row is posted a moment later rather than right away: closing several
 * resources in a row then costs one request instead of one each, which is what
 * the endpoint asks for, while a single close still reports within seconds.
 */
export const endActivity = (viewId: number) => {
  const view = openViews.get(viewId);
  if (!view) return;

  openViews.delete(viewId);
  queueActivity(toItem(view, Date.now()));
  scheduleFlush();
};

/**
 * End everything still open and send it immediately. Used when the page goes
 * away, where there is no time for the scheduled flush.
 */
export const endAndFlushActivities = ({ unload = false } = {}) => {
  const endedAt = Date.now();
  openViews.forEach(view => queueActivity(toItem(view, endedAt)));
  openViews.clear();
  flushActivities({ unload });
};
